import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';

/** The query value that opens the creation sheet on the SmartLinks page with a catalog song picked. */
export const SMART_LINK_SHEET_SONG_PARAM = 'catalogItemUuid';

/** The query value that names where the sheet was opened from. It stays in the address, so a reload keeps it. */
export const SMART_LINK_SHEET_FROM_PARAM = 'from';

/**
 * Where the sheet was opened from. `addSong`: the Done step of the add-song flow. A page made from
 * there is published at once (owner decision 6, fym-shared `smartlinks/wizardPublish.ts`).
 */
export type SmartLinkSheetFrom = 'addSong';

/**
 * The path that opens the SmartLinks page with the creation sheet open on one catalog song:
 * `/smartlinks?catalogItemUuid=<uuid>[&accountId=<id>][&from=addSong]`. The router adds `/app`.
 */
export function smartLinkSheetSongPath(
  catalogItemUuid: string,
  accountId?: number | null,
  from?: SmartLinkSheetFrom
): string {
  const query = new URLSearchParams();
  query.set(SMART_LINK_SHEET_SONG_PARAM, catalogItemUuid);
  if (accountId !== undefined && accountId !== null) {
    query.set('accountId', String(accountId));
  }
  if (from !== undefined) {
    query.set(SMART_LINK_SHEET_FROM_PARAM, from);
  }
  return `/${SMLRouteNames.smartlinks}?${query.toString()}`;
}

/**
 * The song to open the sheet on, read from the address. Only the SmartLinks page itself counts:
 * the pre-save wizard (`/smartlinks/presaveLink`) reads the same query value for its own form.
 */
export function smartLinkSheetSongOf(pathname: string, search: string): string | undefined {
  const root = `/${SMLRouteNames.smartlinks}`;
  if (pathname !== root && pathname !== `${root}/`) {
    return undefined;
  }
  const value = new URLSearchParams(search).get(SMART_LINK_SHEET_SONG_PARAM);
  return value !== null && value !== '' ? value : undefined;
}

/** True when the address opens the sheet on a song from the add-song flow. */
export function isSheetFromAddSong(pathname: string, search: string): boolean {
  return (
    smartLinkSheetSongOf(pathname, search) !== undefined &&
    new URLSearchParams(search).get(SMART_LINK_SHEET_FROM_PARAM) === 'addSong'
  );
}

/** The search part without the song value and its origin, so a closed sheet does not open again on Reload. */
export function searchWithoutSheetSong(search: string): string {
  const query = new URLSearchParams(search);
  query.delete(SMART_LINK_SHEET_SONG_PARAM);
  query.delete(SMART_LINK_SHEET_FROM_PARAM);
  const rest = query.toString();
  return rest === '' ? '' : `?${rest}`;
}
